'use client'

import React from 'react'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogAppCTA, BlogWhatTheAppChecks, BlogSources, BlogTLDR, BlogKeyTakeaways, BlogFAQ } from '@/app/components/blog/BlogAeo'
import Link from 'next/link'

export default function Post14Content() {
  return (
    <BlogPostLayout
      title="Microplastics in Your Home: Hidden Sources and How to Eliminate Them"
      author="MicroPlastics Team"
      date="October 1, 2025"
      readTime="10 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="MicroPlastics app tracking home exposure"
    >
      <BlogTLDR>
        Your home contains <strong>3-15× more microplastics in the air than
        outdoor air</strong>. Major sources, room by room: kitchen (plastic
        cookware, cutting boards, food storage, microwave), bedroom
        (polyester bedding, synthetic mattress, pillows), living room
        (synthetic carpet, upholstered furniture), bathroom (cosmetics,
        toothpaste, synthetic towels), nursery (toys, wipes, clothing).
        The swaps worth making first target the kitchen, then bedroom and
        living-room textiles.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Indoor air contains 3-15× more microplastic than outdoor air.</>,
          <>Kitchen: highest exposure flux (plastic cookware, microwaving plastic, cutting boards).</>,
          <>Bedroom: polyester bedding sheds microfibres throughout 8 hours of nightly sleep, closed-room concentration.</>,
          <>Living room: synthetic carpet and upholstered furniture continuously shed onto settled dust.</>,
          <>Top 5 swaps: glass food storage, stainless cookware, HEPA air filter, natural-fibre bedding, hard floors.</>,
          <>Priority order: never microwave in plastic, run HEPA and ventilate, hard floors and natural fibres, air out the car. Everything else is a rounding error.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Introduction: The Invisible Contamination Inside Your Home
      </h2>
      <p>
        When most people think about pollution, they picture smog-filled city skylines, factory smokestacks, or plastic-littered beaches. Rarely do they consider the air inside their own living room. Yet a growing body of scientific research reveals a disturbing reality: your home is one of the most significant sources of daily microplastic exposure. From the carpet beneath your feet to the food containers in your kitchen cabinets, microplastics are silently shedding, fragmenting, and becoming airborne in virtually every room of your house.
      </p>
      <p>
        Studies estimate that the average person may inhale more than <strong>7,000 microplastic particles per day</strong> just from indoor air alone. That number does not even account for the microplastics we ingest through food and beverages or absorb through skin contact with contaminated products. Indoor environments tend to have significantly higher concentrations of airborne microplastics than outdoor air because of the abundance of synthetic materials in modern homes and the relatively poor ventilation in sealed, climate-controlled buildings.
      </p>
      <p>
        The problem is not limited to a single source. Microplastics come from dozens of everyday household items, many of which you would never suspect. Understanding where these particles originate is the essential first step toward reducing your exposure and creating a healthier living environment. In this comprehensive guide, we will walk through every major area of your home, identify the hidden microplastic sources lurking in each, and provide practical, actionable strategies you can use to eliminate or significantly reduce your exposure. For an even broader look at reducing microplastic intake from all sources, see our guide on{' '}
        <Link href="/how-to-avoid-microplastics" className="text-[#1E6B47] hover:underline font-medium">
          how to avoid microplastics
        </Link>.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        2026: The Two Rooms That Dominate Household Exposure
      </h2>
      <p>
        Recent measurements let us stop treating every room as equal. Two settings account
        for a disproportionate share of what a household actually takes in.
      </p>
      <p>
        <strong>The air you breathe indoors.</strong> Measurements published in{' '}
        <em>PLOS One</em>, counting only the lung-penetrating 1--10 µm fraction, found a
        median of <strong>528 microplastic particles per cubic metre</strong> in homes,
        dominated by <strong>polyethylene</strong> -- and{' '}
        <strong>2,238 per cubic metre in car cabins</strong>, where polyamide from nylon
        carpet and seat fabric took over. Working out to roughly{' '}
        <strong>68,000 lung-penetrating particles inhaled per day</strong>, air is not a
        secondary route. The car, effectively a small sealed textile-lined box, is the worst
        measured environment most people occupy daily.
      </p>
      <p>
        <strong>The kitchen, when heat is involved.</strong> Work in{' '}
        <em>Environmental Science &amp; Technology</em> found containers releasing up to{' '}
        <strong>4.22 million microplastic and 2.11 billion nanoplastic particles per square
        centimetre</strong> within three minutes of microwaving. Whole polypropylene
        containers shed 326,000--534,000 particles over five minutes. Freezing, long storage
        and repeated cap opening all contribute too, though far less than heat.
      </p>
      <p>
        The practical hierarchy that follows is short:{' '}
        <strong>never microwave in plastic; run HEPA filtration and ventilate; favour hard
        floors and natural-fibre textiles; air out the car before a long drive.</strong>{' '}
        Everything else on a typical household checklist is a rounding error next to those
        four.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Indoor Dust: The Biggest Microplastic Reservoir in Your Home
      </h2>
      <p>
        Household dust is far more than a minor annoyance. It is the single largest reservoir of microplastics in your home. Research published in the journal <em>Environmental Science &amp; Technology</em> found that indoor dust can contain up to 39% synthetic polymer content by weight, with the majority consisting of fibers shed from textiles, carpets, and upholstered furniture.
      </p>
      <p>
        Every time you sit on a polyester sofa, walk across a synthetic carpet, fold a load of laundry containing nylon or acrylic clothing, or even make your bed with polyester sheets, microscopic plastic fibers are released into the air. These fibers settle on surfaces throughout your home and accumulate in dust. Because they are so light, they are easily re-suspended into the air by foot traffic, vacuuming, dusting, or simply opening a door.
      </p>
      <p>
        Synthetic carpeting is one of the worst offenders. Nylon, polypropylene, and polyester carpets continuously shed microfibers through normal foot traffic and wear. Wall-to-wall carpeting can generate thousands of fibers per square meter every day. Rugs made from synthetic materials contribute the same problem on a smaller scale. Upholstered furniture with polyester, microfiber, or acrylic fabric faces the same issue, especially in high-use areas like living rooms and bedrooms.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Elimination Strategies for Indoor Dust</h3>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Replace synthetic carpets with hard flooring.</strong> Hardwood, tile, bamboo, or natural linoleum floors dramatically reduce microfiber accumulation. If hard flooring is not an option, choose carpets made from natural fibers like wool or sisal.
        </li>
        <li>
          <strong>Use a vacuum with a HEPA filter.</strong> Standard vacuums can recirculate microplastic-laden dust back into the air. A HEPA-filtered vacuum captures particles as small as 0.3 microns, trapping microplastics instead of dispersing them.
        </li>
        <li>
          <strong>Dust frequently with damp or microfiber cloths.</strong> Dry dusting pushes particles into the air. Damp cloths capture and remove them. Ironically, microfiber cloths are effective here because their structure traps particles, though they do shed fibers themselves and should be replaced with cotton alternatives where practical.
        </li>
        <li>
          <strong>Choose natural-fiber furnishings.</strong> When purchasing new sofas, chairs, curtains, and bedding, opt for cotton, linen, wool, or hemp fabrics. These materials do not shed plastic fibers.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Kitchen: Where Microplastics Contaminate Your Food
      </h2>
      <p>
        The kitchen is ground zero for microplastic ingestion. Many of the tools and materials we use to store, prepare, and cook food are themselves made of plastic, and they shed particles directly into the meals we eat. The heat involved in cooking accelerates this process dramatically.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Non-Stick Cookware</h3>
      <p>
        Non-stick pans coated with polytetrafluoroethylene (PTFE), commonly known by the brand name Teflon, release microplastic and nanoplastic particles when heated, scratched, or worn down through normal use. A 2022 study published in <em>Science of the Total Environment</em> found that a single surface crack on a non-stick pan can release approximately 9,100 microplastic particles. Metal utensils, abrasive sponges, and high-heat cooking all accelerate the degradation of these coatings.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Plastic Cutting Boards</h3>
      <p>
        Every knife stroke across a plastic cutting board carves microscopic grooves that release polyethylene or polypropylene particles directly onto the food you are preparing. Research from the University of North Dakota estimated that a single plastic cutting board can generate tens of millions of microplastic particles annually through normal use. The deeper the grooves and the more worn the board, the higher the contamination.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Food Storage Containers and Plastic Wrap</h3>
      <p>
        Plastic food storage containers, particularly when used to heat food in the microwave, release significant quantities of microplastics into their contents. A 2023 study found that microwaving plastic baby food containers released up to 4.2 million microplastic particles and 2.1 billion nanoplastic particles per square centimeter. Even at room temperature, acidic foods like tomato sauce and citrus-based dishes can leach microplastics from plastic containers. Plastic wrap behaves similarly, especially when it contacts hot food or is used in the microwave.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Tea Bags</h3>
      <p>
        Many modern tea bags are made from nylon or polyethylene terephthalate (PET) mesh rather than paper. A landmark 2019 study from McGill University found that steeping a single plastic tea bag at brewing temperature releases approximately 11.6 billion microplastic and 3.1 billion nanoplastic particles into your cup. Even tea bags that appear to be paper may contain a heat-seal plastic layer.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Kitchen Elimination Strategies</h3>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Switch to cast iron, stainless steel, or ceramic cookware.</strong> These materials do not shed plastic particles and are far more durable than non-stick coatings.
        </li>
        <li>
          <strong>Replace plastic cutting boards with wood or bamboo.</strong> Wooden cutting boards do not release microplastics and have natural antimicrobial properties.
        </li>
        <li>
          <strong>Store food in glass or stainless steel containers.</strong> Pyrex, mason jars, and stainless steel containers are excellent alternatives. Never microwave food in plastic containers.
        </li>
        <li>
          <strong>Ditch plastic wrap for beeswax wraps or silicone lids.</strong> Beeswax wraps are reusable, compostable, and completely plastic-free. Silicone lids provide a tight seal without microplastic shedding.
        </li>
        <li>
          <strong>Choose loose-leaf tea or paper-only tea bags.</strong> If you use bagged tea, verify the bags are made entirely from paper and natural fibers with no plastic sealant.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Bathroom: Personal Care and Plastic Contamination
      </h2>
      <p>
        Your bathroom is filled with products that contain or shed microplastics, often applied directly to your skin, hair, and mouth. The warm, humid environment and direct body contact make this one of the most intimate exposure points in your home.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Synthetic Loofahs and Sponges</h3>
      <p>
        The brightly colored mesh shower poufs and synthetic loofahs found in most bathrooms are made from nylon or polyethylene netting. As you scrub, these materials shed microfibers directly onto your wet skin. The abrasive action designed to exfoliate your skin simultaneously breaks down the synthetic mesh, releasing particles that wash down the drain and into waterways while also leaving residue on your body.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Shower Curtains</h3>
      <p>
        PVC (polyvinyl chloride) shower curtains are a well-known source of indoor plastic contamination. New PVC curtains off-gas volatile organic compounds and phthalates, and over time they become brittle and shed microplastic particles into the humid bathroom air. Even non-PVC plastic shower curtains made from PEVA or{' '}
        <Link href="/materials/eva" className="text-[#1E6B47] hover:underline font-medium">EVA</Link>{' '}
        can degrade and release particles, though at lower rates than PVC.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Cosmetics and Personal Care Products</h3>
      <p>
        Microplastics are intentionally added to many cosmetics and personal care products as fillers, binders, emulsifiers, and exfoliants. Common culprits include facial scrubs containing polyethylene microbeads, foundations and concealers with nylon-12 or polymethyl methacrylate, toothpaste with polyethylene beads, shampoos with dimethicone (a silicone polymer), and glitter in makeup, body lotions, and nail polish. These products place microplastics directly on your skin, in your mouth, and in your hair, providing a highly efficient absorption pathway. For a deeper look at microplastics in beauty products, our{' '}
        <Link href="/blog/microplastics-health-effects" className="text-[#1E6B47] hover:underline font-medium">
          microplastics and health effects
        </Link>{' '}
        guide covers the biological consequences of this exposure.
      </p>

      <p>
        Two fixtures in the room are worth scoring on their own, because both
        sit between you and water you use every day: our{' '}
        <Link href="/products/shower-head-filter" className="text-[#1E6B47] hover:underline font-medium">
          exposure score for shower head filters
        </Link>{' '}
        covers the housing and cartridge media, and the{' '}
        <Link href="/products/ice-cube-tray-plastic" className="text-[#1E6B47] hover:underline font-medium">
          score for plastic ice cube trays
        </Link>{' '}
        covers the one kitchen item that combines flexible polymer with
        repeated freeze-and-flex stress.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Bathroom Elimination Strategies</h3>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Replace synthetic loofahs with natural alternatives.</strong> Natural sea sponges, organic cotton washcloths, or natural loofah gourds provide the same exfoliation without shedding plastic fibers.
        </li>
        <li>
          <strong>Switch to a fabric or glass shower curtain.</strong> Hemp or organic cotton shower curtains with a water-resistant liner are durable, washable, and plastic-free. A glass shower door eliminates the issue entirely.
        </li>
        <li>
          <strong>Audit your cosmetics for microplastic ingredients.</strong> Check product labels for polyethylene (PE), polypropylene (PP), polymethyl methacrylate (PMMA), nylon-12, and polytetrafluoroethylene (PTFE). The MicroPlastics app can scan products instantly to identify hidden microplastic content.
        </li>
        <li>
          <strong>Choose natural, plastic-free personal care products.</strong> Many brands now offer microplastic-free cosmetics, toothpastes, and skin care products. Look for certifications from organizations like Beat the Microbead.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Laundry Room: The Microfiber Shedding Machine
      </h2>
      <p>
        Your washing machine is one of the most prolific microplastic generators in your home. Every wash cycle involving synthetic fabrics, including polyester, nylon, acrylic, and spandex, releases hundreds of thousands to millions of microfibers into the wash water. A single load of polyester laundry can shed up to 700,000 microfibers per wash, according to research from Plymouth University.
      </p>
      <p>
        These fibers are so small that they pass through most wastewater treatment plants and end up in rivers, lakes, and oceans. But they also contaminate your home environment. When you move wet synthetic clothing from the washer to the dryer, fibers become airborne. The dryer itself generates enormous amounts of microfiber lint. Every time you open the dryer door or clean the lint trap, you release a cloud of microplastic particles into your laundry room and beyond.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Laundry Elimination Strategies</h3>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Install a washing machine microfiber filter.</strong> External lint filters like the Filtrol or PlanetCare filter attach to your washing machine drain hose and capture up to 90% of microfibers before they enter the water supply.
        </li>
        <li>
          <strong>Use a microfiber-catching laundry bag.</strong> Bags like the Guppyfriend trap synthetic fibers inside the bag during the wash cycle, preventing them from entering the water.
        </li>
        <li>
          <strong>Wash synthetic clothing less frequently and on cold, gentle cycles.</strong> Higher temperatures, longer cycles, and aggressive agitation increase fiber shedding. Cold water and delicate settings reduce microfiber release by up to 30%.
        </li>
        <li>
          <strong>Transition your wardrobe to natural fibers.</strong> Gradually replace polyester, nylon, and acrylic garments with cotton, wool, linen, hemp, or Tencel alternatives. Natural fibers biodegrade and do not contribute to microplastic pollution.
        </li>
        <li>
          <strong>Air dry when possible.</strong> Line drying or using a drying rack eliminates the dryer as a source of airborne microfiber lint.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        HVAC Systems and Indoor Air Quality
      </h2>
      <p>
        Your home heating, ventilation, and air conditioning (HVAC) system plays a dual role in the microplastics problem. On one hand, it can circulate microplastic-laden air throughout your home, spreading particles from one room to every other room connected to the ductwork. On the other hand, a properly maintained HVAC system with high-quality filters can be one of your most effective tools for reducing airborne microplastic exposure.
      </p>
      <p>
        Standard fiberglass HVAC filters are designed to protect the equipment, not your lungs. They capture large debris like pet hair and dust bunnies but allow microplastic fibers and fine particles to pass through easily. These particles then recirculate through your home every time the system cycles on, potentially increasing concentrations in rooms far from the original source.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">HVAC and Air Quality Strategies</h3>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Upgrade to MERV-13 or higher HVAC filters.</strong> MERV-13 filters capture particles as small as 0.3 to 1.0 microns, which includes most airborne microplastic fibers. Replace them every 60 to 90 days for optimal performance.
        </li>
        <li>
          <strong>Use standalone HEPA air purifiers.</strong> Place HEPA air purifiers in bedrooms and living areas where you spend the most time. True HEPA filters remove 99.97% of particles at 0.3 microns, making them highly effective against airborne microplastics.
        </li>
        <li>
          <strong>Ventilate your home regularly.</strong> Open windows when outdoor air quality permits to dilute indoor microplastic concentrations. Indoor air can contain 2 to 5 times more microplastics than outdoor air in many environments.
        </li>
        <li>
          <strong>Have your ductwork professionally cleaned.</strong> Over time, microplastic fibers and synthetic dust accumulate inside HVAC ducts. Professional duct cleaning removes this reservoir of contamination.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Cleaning Products: Microbeads and Hidden Plastics
      </h2>
      <p>
        While the Microbead-Free Waters Act of 2015 banned plastic microbeads from rinse-off cosmetics in the United States, microbeads and other microplastic ingredients persist in many household cleaning products that were not covered by the legislation. Some abrasive kitchen and bathroom cleaners still contain polyethylene or polypropylene particles as scrubbing agents. Certain laundry detergent pods use water-soluble polyvinyl alcohol (PVA) casings that, while designed to dissolve, may not fully break down in wastewater treatment and can contribute to microplastic pollution.
      </p>
      <p>
        Disposable cleaning wipes, whether marketed for kitchens, bathrooms, or floors, are typically made from polyester and polypropylene nonwoven fabrics. Every wipe sheds plastic fibers onto the surfaces you are ostensibly cleaning. Scouring pads and synthetic sponges made from melamine foam or polyester also degrade over time, releasing microplastic particles onto your dishes and countertops.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Cleaning Product Strategies</h3>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Switch to plant-based, plastic-free cleaning products.</strong> Choose liquid or powdered cleaners in glass or metal containers from brands that disclose all ingredients and avoid synthetic polymers.
        </li>
        <li>
          <strong>Replace disposable wipes with reusable cotton cloths.</strong> Organic cotton rags or Swedish dishcloths (made from cellulose and cotton) are washable, biodegradable, and effective.
        </li>
        <li>
          <strong>Use natural scouring pads.</strong> Coconut coir scrubbers, copper scrubbers, or natural cellulose sponges clean effectively without shedding plastic particles.
        </li>
        <li>
          <strong>Avoid plastic-coated detergent pods.</strong> Opt for powdered detergent in cardboard boxes, concentrated liquid detergent in glass bottles, or detergent sheets made from natural ingredients.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Furniture and Soft Furnishings: The Slow Shedders
      </h2>
      <p>
        Most modern furniture contains significant amounts of synthetic materials that contribute to chronic, low-level microplastic exposure. Polyester-upholstered sofas and chairs shed microfibers every time someone sits down or gets up. Polyurethane foam cushions, found in the vast majority of couches, mattresses, and throw pillows, slowly degrade over the years, releasing microplastic and chemical particles into the air and dust.
      </p>
      <p>
        Memory foam mattresses and pillows are made from polyurethane, often combined with chemical flame retardants and other additives. As these foams age, they break down and release both microplastic particles and volatile organic compounds. Synthetic throw blankets, decorative pillows with polyester fill, and acrylic curtains all contribute to the household microplastic burden. Even some wooden furniture uses polyester veneers or plastic laminate surfaces that degrade over time.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Furniture Strategies</h3>
      <ul className="list-disc pl-6 space-y-3">
        <li>
          <strong>Choose furniture with natural upholstery.</strong> Look for sofas and chairs covered in cotton, linen, wool, or leather rather than polyester or microfiber fabric.
        </li>
        <li>
          <strong>Opt for natural-fill cushions and mattresses.</strong> Latex (natural rubber), organic cotton, wool, and kapok are all excellent alternatives to polyurethane foam. Certified organic mattresses eliminate both microplastic shedding and chemical flame retardant exposure.
        </li>
        <li>
          <strong>Cover existing synthetic furniture.</strong> If replacing furniture is not immediately practical, use tightly woven natural-fiber slipcovers to contain microfiber shedding from polyester upholstery.
        </li>
        <li>
          <strong>Choose solid wood furniture over plastic laminate.</strong> Solid wood, metal, and glass furniture pieces do not degrade into microplastic particles.
        </li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Creating a Whole-Home Microplastic Reduction Plan
      </h2>
      <p>
        Eliminating all microplastic sources from your home overnight is neither practical nor necessary. The most effective approach is a systematic, room-by-room reduction plan that prioritizes the highest-impact changes first and works through lower-priority items over time.
      </p>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Immediate High-Impact Changes</h3>
      <ul className="list-disc pl-6 space-y-2">
        <li>Stop microwaving food in plastic containers. Switch to glass or ceramic immediately.</li>
        <li>Replace plastic cutting boards with wood or bamboo.</li>
        <li>Upgrade your HVAC filter to MERV-13 or higher.</li>
        <li>Place a HEPA air purifier in your bedroom, where you spend approximately one-third of your day.</li>
        <li>Switch to loose-leaf tea or verified paper-only tea bags.</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Short-Term Changes (Within One to Three Months)</h3>
      <ul className="list-disc pl-6 space-y-2">
        <li>Replace non-stick cookware with cast iron, stainless steel, or ceramic.</li>
        <li>Switch food storage from plastic to glass or stainless steel containers.</li>
        <li>Install a microfiber filter on your washing machine.</li>
        <li>Replace synthetic loofahs and cleaning sponges with natural alternatives.</li>
        <li>Audit and replace personal care products containing microplastics.</li>
      </ul>

      <h3 className="text-xl font-semibold text-gray-800 mt-6">Long-Term Changes (Over Six to Twelve Months)</h3>
      <ul className="list-disc pl-6 space-y-2">
        <li>Gradually replace synthetic clothing with natural-fiber alternatives.</li>
        <li>Replace synthetic carpets with hard flooring or natural-fiber rugs.</li>
        <li>Transition to natural-fill mattresses and pillows.</li>
        <li>Replace PVC shower curtains with fabric or glass alternatives.</li>
        <li>Upgrade furniture upholstery and cushions to natural materials as items reach end of life.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The Bottom Line
      </h2>
      <p>
        Your home should be a sanctuary, but right now it is likely one of your most significant sources of microplastic exposure. From the synthetic fibers shedding off your couch to the plastic particles leaching from your food containers, microplastics are present in virtually every room and every activity of daily life. We may be inhaling over 7,000 microplastic particles per day from indoor air alone, which is reason enough to act.
      </p>
      <p>
        The good news is that you are not powerless. Every synthetic product you replace with a natural alternative, every HEPA filter you install, and every plastic container you swap for glass is a meaningful reduction in your family&apos;s daily microplastic dose. You do not need to overhaul your entire home in a single weekend. Start with the highest-impact changes, like stopping microwave use with plastic and upgrading your air filtration, and build from there.
      </p>
      <p>
        The MicroPlastics app can help you make smarter choices by scanning products for microplastic content before you bring them into your home. Combined with the room-by-room strategies outlined above, you can systematically transform your living space from a source of microplastic exposure into a cleaner, healthier environment for you and your family.
      </p>

      <BlogWhatTheAppChecks
        items={[
          <>Material, stainless, glass, ceramic, cast iron, plastic (PE / PP / PS / PVC), silicone, wood.</>,
          <>Visible condition, scratches, chips, warping, fade.</>,
          <>Brand and product line, flags for non-stick / PFAS-treated items.</>,
          <>Use-context flags you log, heat exposure, dishwasher cycles, contact with hot or fatty food.</>,
          <>Cited published research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan kitchen and household products before buying"
        sub="Cookware, food storage, cutting boards, accessories. The app weighs material, condition, brand, and use-context to give a 0–100 risk score per item."
        buttonLabel="Scan household items in the app"
      />


      <BlogFAQ
        items={[
          {
            question: 'Where are the most microplastics in my home?',
            answer:
              'Indoor air typically has 3-15× more microplastic concentration than outdoor air. The biggest sources by location: kitchen (cookware, cutting boards, microwaving plastic), bedroom (polyester bedding and mattress), living room (synthetic carpet and upholstered furniture), bathroom (cosmetics and synthetic towels).',
          },
          {
            question: 'What is the most impactful change to reduce microplastics at home?',
            answer:
              'Stop microwaving food in plastic. 4.22 million particles per cm² in 3 minutes is the highest-flux event in a typical home. Glass and ceramic for any reheating. Combine with filtered water in glass for the biggest single-day reduction available.',
          },
          {
            question: 'Do air purifiers reduce indoor microplastics?',
            answer:
              'Yes: a HEPA 13+ purifier sized for the room captures 99.97% of particles down to 0.3 μm, which includes most microplastic fibres. Run continuously in the bedroom and main living area for the largest exposure reduction.',
          },
          {
            question: 'Should I replace synthetic carpet?',
            answer:
              'Long-term, yes, synthetic carpet (polyester, nylon, polypropylene) continuously sheds microfibres for its entire lifespan. Short-term: vacuum often with a sealed-bag HEPA vacuum, use natural-fibre rugs over hard floors when possible, ventilate well.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Yakovenko N, et al.',
            year: 2025,
            title: 'Human exposure to PM10 microplastics in indoor air',
            publisher: 'PLOS One',
            url: 'https://journals.plos.org/plosone/article?id=10.1371%2Fjournal.pone.0328011',
          },
          {
            authors: 'Hussain KA, Romanova S, Okur I, et al.',
            year: 2023,
            title: 'Microplastic release from plastic containers under microwave heating',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.3c01942',
          },
          {
            authors: 'Yadav H, Khan MRH, Quadir M, et al.',
            year: 2023,
            title: 'Cutting Boards: An Overlooked Source of Microplastics in Human Food',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.3c00924',
          },
          {
            authors: 'Stapleton HM, Klosterhaus S, Eagle S, et al.',
            year: 2009,
            title: 'Detection of organophosphate flame retardants in furniture foam and house dust',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/es9014019',
          },
          {
            authors: 'US EPA',
            year: 2024,
            title: 'Per- and Polyfluoroalkyl Substances (PFAS): health effects and exposure',
            publisher: 'US EPA',
            url: 'https://www.epa.gov/pfas',
          },
          {
            authors: 'De Falco F, Cocca M, Avella M, Lettieri E',
            year: 2020,
            title: 'Microfiber release to water, via laundering, and to air, via everyday use',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.9b06892',
          },
        ]}
      />


    </BlogPostLayout>
  )
}
